import { useActivationGroupsQuery } from '@/features/activationGroup/hooks/useActivationGroupsQuery'
import { useAvailableActivationGroupsQuery } from '@/features/activationGroup/hooks/useAvailableActivationGroupsQuery'
import { ActivationGroupState } from '@/features/activationGroup/types'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import {
  AMPTANK_AUTOMATED_MFRR_BIDDING_KILLSWITCH_TOGGLE,
  isAutoTradedAmptankActivationGroup,
} from '@/features/bidding/utils/autoTradedRegulationObjects'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'

type Options = {
  enabled?: boolean
}

export function useBidOfferEligibleActivationGroups(groupParams: GroupedBidParams, options?: Options) {
  const { isEnabled } = useFeatureToggle()

  const filteredGroups = isEnabled('FLEX_PORTAL_FILTER_CONFLICTING_ACTIVATION_GROUPS')
    ? getAvailableGroups(groupParams, options)
    : getAllLiveGroups(groupParams, options)

  // BID-279: portfolio-manager cannot see the Amptank autotrader's killswitch (it lives in the
  // trading Unleash), so its autotraded ROs are hidden client-side unless our mirror killswitch
  // says the bot is dead — a missing flag means hidden. See autoTradedRegulationObjects.ts.
  const eligibleActivationGroups = isEnabled(AMPTANK_AUTOMATED_MFRR_BIDDING_KILLSWITCH_TOGGLE)
    ? filteredGroups
    : filteredGroups?.filter(
        (group) => !isAutoTradedAmptankActivationGroup(group, groupParams.marketProgram, groupParams.bidType),
      )

  return {
    eligibleActivationGroups,
    isLoading: !filteredGroups && (options?.enabled ?? true),
  }
}

function getAllLiveGroups(groupParams: GroupedBidParams, options?: Options) {
  const { selectedCountry } = useBiddingContext()
  const { activationGroups } = useActivationGroupsQuery(
    { countryCode: selectedCountry },
    { enabled: options?.enabled ?? true },
  )

  return activationGroups
    ? activationGroups
        .filter(
          (activationGroup) =>
            activationGroup.marketProgram === groupParams.marketProgram &&
            activationGroup.portfolio.code === groupParams.portfolioCode &&
            activationGroup.state === ActivationGroupState.LIVE,
        )
        .toSorted((a, b) => a.code.localeCompare(b.code))
    : undefined
}

function getAvailableGroups(groupParams: GroupedBidParams, options?: Options) {
  const { activationGroups } = useAvailableActivationGroupsQuery(
    {
      portfolioCode: groupParams.portfolioCode,
      deliveryDay: groupParams.deliveryDay,
      marketProgram: groupParams.marketProgram,
      bidType: groupParams.bidType,
    },
    { enabled: options?.enabled ?? true },
  )

  return activationGroups ? activationGroups.toSorted((a, b) => a.code.localeCompare(b.code)) : undefined
}
